import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import App from './App';
import { useAuthStore } from './store/auth';
import { XpProvider } from './lib/xp-context';
import { IngestionProvider } from './lib/ingestion-context';
import { initTheme } from './lib/theme';
import './styles.css';

// Apply stored theme + follow OS dark mode changes in system mode.
initTheme();

// Cognito User Pool — direct SRP/USER_PASSWORD auth, no Hosted UI redirect.
const userPoolId       = import.meta.env.VITE_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
if (!userPoolId || !userPoolClientId) {
  // Fail loudly if Vite didn't substitute the env vars at build time
  // — this is much clearer than Amplify's runtime "UserPool not configured" error.
  // eslint-disable-next-line no-console
  console.error('Cognito env vars missing at build time', { userPoolId, userPoolClientId });
}
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId:       userPoolId!,
      userPoolClientId: userPoolClientId!,
      signUpVerificationMethod: 'code',
      loginWith: { email: true },
    },
  },
});

// Bootstrap auth store on app load (does an initial fetchAuthSession to detect existing session)
useAuthStore.getState().init();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <XpProvider>
          <IngestionProvider>
            <App />
          </IngestionProvider>
        </XpProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
